// src/components/VideoCallComponent.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ChatIcon from '@mui/icons-material/Chat';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import FlipCameraIcon from '@mui/icons-material/FlipCameraIos';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

// ---------------- Styled ----------------
const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100dvh',
  minHeight: '-webkit-fill-available',
  backgroundColor: theme.palette.grey[900],
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const MainVideoBox = styled(Box)({
  flex: 1,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
});

const VideoElement = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  backgroundColor: '#000',
});

const LocalVideoBox = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: `calc(${theme.spacing(14)} + env(safe-area-inset-bottom))`,
  right: `calc(${theme.spacing(2)} + env(safe-area-inset-right))`,
  width: 'min(42vw, 240px)',
  height: 'calc(min(42vw, 240px) * 0.75)',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8],
  zIndex: 10,
  [theme.breakpoints.down('sm')]: {
    width: 120,
    height: 90,
    right: `calc(${theme.spacing(2)} + env(safe-area-inset-right))`,
    bottom: `calc(${theme.spacing(14)} + env(safe-area-inset-bottom))`,
  }
}));

const ControlsBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: `calc(${theme.spacing(3)} + env(safe-area-inset-bottom)) ${theme.spacing(3)}`,
  background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
  zIndex: 120,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    gap: theme.spacing(1),
  }
}));

const ControlButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.common.white,
  width: 'clamp(48px, 10vw, 64px)',
  height: 'clamp(48px, 10vw, 64px)',
  transition: 'transform 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.grey[700],
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 48,
    height: 48,
  }
}));

const EndCallButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  width: 56,
  height: 56,
  transition: 'transform 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    transform: 'scale(1.05)',
  }
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  borderRadius: 16,
  fontWeight: 500,
  boxShadow: theme.shadows[1],
}));

// ---------------- Helper URL WS ----------------
function buildWsUrl(roomId) {
  const { protocol, host, hostname } = window.location;
  const isSecure = protocol === 'https:';
  const wsProtocol = isSecure ? 'wss://' : 'ws://';
  let wsHost = host; // producción: mismo host por Nginx
  if (!isSecure && (hostname === 'localhost' || hostname === '127.0.0.1')) {
    wsHost = 'localhost:8000'; // dev: backend Django
  }
  return `${wsProtocol}${wsHost}/ws/telemedicine/${roomId}/`;
}

// ---------------- Helpers: TURN + ICE logs ----------------
const TURN_HOST = '50.6.7.49'; // Ajusta si usas dominio en vez de IP

async function fetchTurnCredentials() {
  try {
    const res = await fetch('/api/turn-credentials/');
    if (!res.ok) throw new Error('No se pudieron obtener credenciales TURN');
    const data = await res.json();
    console.log('[TURN] Credenciales obtenidas:', data.username);
    return data;
  } catch (e) {
    console.error('[TURN] Error obteniendo credenciales:', e);
    return null;
  }
}

function candidateTypeFromStr(candStr = '') {
  const m = candStr.match(/typ\s(\w+)/);
  return m ? m[1] : 'unknown';
}

const VideoCallComponent = ({ roomId, patientName, doctorName, onEndCall, isPatient = false }) => {
  const theme = useTheme();

  // ---------------- State ----------------
  const [facingMode, setFacingMode] = useState('environment');
  const [showOrientationMessage, setShowOrientationMessage] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // WebRTC status
  const [endCallDialogOpen, setEndCallDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [waitingTime, setWaitingTime] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const [screenShareSupported, setScreenShareSupported] = useState(true);
  const [remoteAutoMuted, setRemoteAutoMuted] = useState(true);
  const [needsReplay, setNeedsReplay] = useState(false);
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [pendingMessages, setPendingMessages] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [debugInfo, setDebugInfo] = useState({
    localStream: null,
    remoteStream: null,
    connectionState: 'new',
    iceConnectionState: 'new',
    iceGatheringState: 'new',
    signalingState: 'stable',
    localDescription: null,
    remoteDescription: null,
    iceCandidates: [],
    webSocketState: 'connecting'
  });
  const [isPolite, setIsPolite] = useState(false);

  const safePatientName = patientName || 'Paciente';
  const safeDoctorName = doctorName || 'Doctor';
  const participantName = isPatient ? safeDoctorName : safePatientName;
  const roleLabel = isPatient ? 'Doctor' : 'Paciente';

  // ---------------- Refs ----------------
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const wsRef = useRef(null);
  const originalVideoTrackRef = useRef(null);
  const screenStreamRef = useRef(null);
  const chatContainerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const iceCandidatesQueue = useRef([]);
  const debugIntervalRef = useRef(null);
  const makingOfferRef = useRef(false);
  const ignoreOfferRef = useRef(false);
  const isSettingRemoteAnswerPendingRef = useRef(false);

  // Fábrica de RTCPeerConnection via ref (evita hoisting/scope issues)
  const createPeerConnRef = useRef(null);

  // Señal de presencia remota y oferta pendiente hasta que el otro se una
  const remoteReadyRef = useRef(false);
  const pendingOfferRef = useRef(false);

  // Para estancamiento
  const stagnationTimeoutRef = useRef(null);

  const instanceIdRef = useRef(crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  // ---------------- Chat persist ----------------
  const [chatMessages, setChatMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(`chat_messages_${roomId}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (chatMessages.length > 0 && roomId) {
      try {
        localStorage.setItem(`chat_messages_${roomId}`, JSON.stringify(chatMessages));
      } catch {}
    }
  }, [chatMessages, roomId]);

  // ---------------- Feature detection ----------------
  useEffect(() => {
    const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
    setScreenShareSupported(isSupported);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) setScreenShareSupported(false);
  }, []);

  // ---------------- Permissions check ----------------
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        stream.getTracks().forEach(t => t.stop());
      } catch (error) {
        setErrorMessage(`No se pudo acceder a la cámara o micrófono: ${error.message}. Verifique permisos.`);
      }
    };
    checkPermissions();
  }, []);

  // ---------------- Orientation helper ----------------
  useEffect(() => {
    const checkOrientation = () => {
      const isPortrait = window.innerHeight > window.innerWidth && window.innerWidth < 768;
      setShowOrientationMessage(isPortrait);
      if (isPortrait) {
        setTimeout(() => setShowOrientationMessage(false), 5000);
      }
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  // ---------------- Waiting time ----------------
  useEffect(() => {
    let timer;
    if (connectionStatus !== 'connected') {
      timer = setInterval(() => setWaitingTime(prev => prev + 1), 1000);
    }
    return () => timer && clearInterval(timer);
  }, [connectionStatus]);

  const formatWaitingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // ---------------- Periodic RTC state check ----------------
  useEffect(() => {
    const interval = setInterval(() => {
      if (peerConnectionRef.current) {
        const connectionState = peerConnectionRef.current.connectionState;
        const iceState = peerConnectionRef.current.iceConnectionState;
        console.log("Verificación periódica - Estado:", connectionState, "ICE:", iceState);
        if (connectionState === 'failed' && (iceState === 'connected' || iceState === 'completed')) {
          handleFailedButIceConnected();
        }
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [isPatient]);

  // ---------------- Periodic WS check ----------------
  useEffect(() => {
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.log("WebSocket no está abierto, intentando reconectar...");
        initializeWebSocket();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // ---------------- Debug ----------------
  useEffect(() => {
    debugIntervalRef.current = setInterval(() => {
      if (peerConnectionRef.current) {
        setDebugInfo(prev => ({
          ...prev,
          connectionState: peerConnectionRef.current.connectionState,
          iceConnectionState: peerConnectionRef.current.iceConnectionState,
          iceGatheringState: peerConnectionRef.current.iceGatheringState,
          signalingState: peerConnectionRef.current.signalingState,
          localDescription: peerConnectionRef.current.localDescription,
          remoteDescription: peerConnectionRef.current.remoteDescription,
          localStream: localStreamRef.current ? {
            videoTracks: localStreamRef.current.getVideoTracks().length,
            audioTracks: localStreamRef.current.getAudioTracks().length
          } : null,
          remoteStream: remoteVideoRef.current?.srcObject ? {
            videoTracks: remoteVideoRef.current.srcObject.getVideoTracks().length,
            audioTracks: remoteVideoRef.current.srcObject.getVideoTracks().length
          } : null,
          webSocketState: wsRef.current?.readyState || 3
        }));
      }
    }, 1000);
    return () => debugIntervalRef.current && clearInterval(debugIntervalRef.current);
  }, []);

  // ---------------- Lifecycle ----------------
  useEffect(() => {
    initializeWebRTC();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // ---------------- Audio Watchdog ----------------
  useEffect(() => {
    const attachAudioRecovery = () => {
      const pc = peerConnectionRef.current;
      const audioSender = pc?.getSenders()?.find(s => s.track?.kind === 'audio');
      const at = audioSender?.track;
      if (!at) return;

      at.onended = async () => {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
          const newAudioTrack = audioStream.getAudioTracks()[0];
          if (newAudioTrack) {
            await audioSender.replaceTrack(newAudioTrack);
            const s = localStreamRef.current || new MediaStream();
            s.addTrack(newAudioTrack);
            localStreamRef.current = s;
            if (localVideoRef.current) localVideoRef.current.srcObject = s;
          }
        } catch (e) {
          console.warn('Recuperación de audio falló:', e);
        }
      };
    };
    attachAudioRecovery();
  }, []);


  // ---------------- Estancamiento ('new') ----------------
  useEffect(() => {
    if (connectionStatus === 'connecting') {
      stagnationTimeoutRef.current = setTimeout(() => {
        if (peerConnectionRef.current?.iceConnectionState === 'new') {
          console.warn("[STAGNATION] ICE 'new' >15s. Doctor fuerza restartIce + oferta.");
          if (!isPatient && peerConnectionRef.current) {
            try { peerConnectionRef.current.restartIce(); } catch (e) { console.warn("restartIce falló:", e); }
            if (remoteReadyRef.current) {
              setTimeout(createAndSendOffer, 1000);
            } else {
              pendingOfferRef.current = true;
            }
          }
        }
      }, 15000);
    }
    return () => clearTimeout(stagnationTimeoutRef.current);
  }, [connectionStatus, isPatient]);

  // ---------------- Drain ICE queue ----------------
  const drainQueuedIce = async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    while (iceCandidatesQueue.current.length > 0) {
      const candidate = iceCandidatesQueue.current.shift();
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
        console.log("[ICE] Candidato de cola añadido");
      } catch (e) {
        console.warn("[ICE] Error añadiendo candidato de cola:", e);
      }
    }
  };

  // ---------------- RTCPeerConnection factory (via ref) ----------------
  createPeerConnRef.current = async () => {
    try {
      console.log("Creando conexión peer...");

      // 1) Obtener credenciales TURN dinámicas
      const turnCreds = await fetchTurnCredentials();

      // 2) Construir iceServers priorizando tu COTURN
      const iceServers = [
        { urls: 'stun:stun.l.google.com:19302' },
      ];

      if (turnCreds) {
        iceServers.push({
          urls: [
            `stun:${TURN_HOST}:3478`,
            `turn:${TURN_HOST}:3478?transport=udp`,
            `turn:${TURN_HOST}:3478?transport=tcp`,
            `turns:${TURN_HOST}:5349?transport=tcp`
          ],
          username: turnCreds.username,
          credential: turnCreds.credential
        });
      } else {
        console.warn('[TURN] No hay credenciales. Usando solo STUN y fallback de prueba.');
      }

      // Fallback público opcional (solo para pruebas, puedes quitarlo en prod)
      iceServers.push({
        urls: 'turn:openrelay.metered.ca:80?transport=tcp',
        username: 'openrelayproject',
        credential: 'openrelayproject'
      });

      const forceRelay = new URLSearchParams(window.location.search).get('forceRelay') === '1';

      const configuration = {
        iceServers,
        iceCandidatePoolSize: 20,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        sdpSemantics: 'unified-plan',
        iceTransportPolicy: forceRelay ? 'relay' : 'all',
      };

      console.log('[RTC] Configuración ICE:', configuration);

      const pc = new RTCPeerConnection(configuration);
      peerConnectionRef.current = pc;
      setConnectionStatus('connecting');

      // Añadir tracks locales
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          pc.addTrack(track, localStreamRef.current);
        });
      }

      // Handlers: SOLO el doctor inicia ofertas
      pc.onnegotiationneeded = async () => {
        if (!isPatient) {
          if (!remoteReadyRef.current) {
            console.log('onnegotiationneeded -> remoto NO listo, marco pendingOffer');
            pendingOfferRef.current = true;
            return;
          }
          console.log('onnegotiationneeded -> remoto listo, doctor envía oferta');
          await createAndSendOffer();
        } else {
          console.log('onnegotiationneeded (paciente) -> no iniciar oferta');
        }
      };

      pc.ontrack = (event) => {
        console.log("Track remoto recibido:", event.streams?.[0]?.getTracks()?.map(t => t.kind).join(", "));
        const v = remoteVideoRef.current;

        if (v) {
          const current = v.srcObject;
          if (!current || current.id !== event.streams[0].id) {
            v.srcObject = event.streams[0];
          }

          // Autoplay silencioso
          v.muted = true; // clave para que Chrome permita autoplay
          const tryPlay = (retries = 0) => {
            v.play().catch(err => {
              if (retries < 5) {
                setTimeout(() => tryPlay(retries + 1), 500);
              } else {
                console.warn("No se pudo autoplay remoto (silencioso):", err?.name || err);
              }
            });
          };
          // Intenta reproducir cuando esté listo
          v.onloadedmetadata = () => tryPlay();
          // Y también intenta ya, por si metadata ya cargó
          tryPlay();
        }
      };


      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const t = candidateTypeFromStr(event.candidate.candidate);
          console.log(`[ICE] Generado candidato local [${t}]:`, event.candidate.candidate);
          wsRef.current.send(JSON.stringify({
            type: 'ice-candidate',
            candidate: event.candidate
          }));
        } else if (!event.candidate) {
          console.log("[ICE] Fin de candidatos locales (gathering complete)");
        }
      };

      pc.oniceconnectionstatechange = () => {
        const iceState = pc.iceConnectionState;
        console.log("ICE state:", iceState);

        if (iceState === 'connected' || iceState === 'completed') {
          setConnectionStatus('connected');
          if (remoteVideoRef.current?.paused && remoteVideoRef.current.srcObject) {
            remoteVideoRef.current.play().catch(e => {
              console.warn("Error reproduciendo video remoto:", e);
              setNeedsReplay(true);
            });
          }
          return;
        }

        if (iceState === 'failed' || iceState === 'disconnected') {
          setConnectionStatus('disconnected');
          if (!isPatient) {
            try { pc.restartIce(); } catch (e) { console.warn("restartIce error:", e); }
            setTimeout(() => createAndSendOffer(), 1500);
          } else {
            console.log("Paciente detecta ICE fallido/desconectado -> espera oferta del doctor");
          }
        }
      };

      pc.onconnectionstatechange = () => {
        const cs = pc.connectionState;
        console.log("RTCPeerConnection state:", cs);

        if (cs === 'failed' &&
            (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed')) {
          console.log("failed pero ICE conectado -> mantener 'connected'");
          return;
        }

        setConnectionStatus(cs);

        if (cs === 'failed') {
          if (!isPatient) {
            setTimeout(() => createAndSendOffer(), 1500);
          } else {
            console.log("Paciente ve 'failed' -> espera renegociación del doctor");
          }
        }
      };

      pc.onicegatheringstatechange = () => {
        console.log('ICE gathering state:', pc.iceGatheringState);
      };
    } catch (error) {
      console.error("Error al crear conexión peer:", error);
      setErrorMessage('Error al establecer la conexión. Por favor, recargue la página.');
    }
  };

  // ---------------- Initialize WebRTC ----------------
  const initializeWebRTC = async (retryCount = 0) => {
    try {
      console.log('Inicializando WebRTC...');
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      const videoConstraints = isMobile ? {
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { max: 24 },
        facingMode: { ideal: "environment" }
      } : {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { max: 30 }
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 2
        }
      });

      console.log('Stream local obtenido');
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      initializeWebSocket();
    } catch (error) {
      console.error('Error initializing WebRTC:', error);
      if (retryCount < 2) {
        console.log(`Reintentando inicialización (${retryCount + 1}/3)...`);
        setTimeout(() => initializeWebRTC(retryCount + 1), 2000);
        return;
      }
      setConnectionStatus('error');
      setErrorMessage(`Error al inicializar la videollamada: ${error.message || 'Error desconocido'}`);
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  // ---------------- Initialize WebSocket ----------------
  const initializeWebSocket = () => {
    try {
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        try { wsRef.current.close(); } catch (_) {}
      }

      const wsUrl = buildWsUrl(roomId);
      console.log("Conectando WebSocket a:", wsUrl);

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = async () => {
        console.log('WebSocket conectado exitosamente');
        setErrorMessage('');

        setIsPolite(isPatient);
        console.log(`Este peer es ${isPatient ? 'polite (paciente)' : 'impolite (doctor)'}`);

        wsRef.current.send(JSON.stringify({
          type: 'join',
          user_id: isPatient ? 'patient' : 'doctor',
          role: isPatient ? 'patient' : 'doctor',
          instance_id: instanceIdRef.current
        }));

        if (!createPeerConnRef.current) {
          console.error('createPeerConnRef.current no está asignado.');
          return;
        }
        // IMPORTANTE: Esperamos a crear la RTCPeerConnection (con TURN dinámico)
        await createPeerConnRef.current();

        // IMPORTANTE: NO dispares oferta inicial aquí.
        // Espera a recibir "user_joined" (señal de presencia remota).
      };

      wsRef.current.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("Mensaje WebSocket recibido:", data.type);

          switch (data.type) {
            case 'room_full': {
              console.warn('Sala ocupada:', data.detail || '');
              setErrorMessage('La consulta ya está en curso con otro paciente. Intenta más tarde.');
              try { wsRef.current?.close(); } catch (_) {}
              // No sigas procesando señalización
              return;
            }

            case 'replaced': {
              console.warn('Esta conexión fue reemplazada por una nueva instancia.');
              setErrorMessage('Se abrió esta consulta en otra pestaña/dispositivo. Cerrando esta ventana.');
              setTimeout(() => {
                try { wsRef.current?.close(); } catch (_) {}
                cleanup();
                onEndCall?.(isPatient);
              }, 1200);
              return;
            }

            case 'user_joined': {
              // Presencia para habilitar oferta
              if (!remoteReadyRef.current) {
                remoteReadyRef.current = true;
                // remoteReadyRef ya marca la presencia del remoto
                console.log('[Presence] remoteReady = true');
              }
              if (!isPatient && pendingOfferRef.current) {
                if (peerConnectionRef.current?.signalingState === 'stable') {
                  console.log('[Presence] Enviando oferta pendiente tras user_joined');
                  pendingOfferRef.current = false;
                  await createAndSendOffer();
                } else {
                  console.log('[Presence] No estable todavía (', peerConnectionRef.current?.signalingState, '), mantengo pendingOffer');
                }
              }
              // Si además quieres que lo procese tu handler genérico:
              break;
            }

            default:
              // otros tipos caen al handler estándar
              break;
          }

          // Procesa señalización genérica (offer/answer/ice, chat, etc.)
          await handleSignalingMessage(data);
        } catch (error) {
          console.error("Error procesando mensaje WebSocket:", error);
        }
      };

      let reconnectAttempts = 0;
      const maxReconnectAttempts = 5;

      wsRef.current.onclose = (event) => {
        console.log("WebSocket cerrado:", event.code, event.reason);
        if (event.code === 1006) {
          setErrorMessage("La conexión se cerró de forma inesperada. Intentando reconectar...");
        } else if (event.code === 1011) {
          setErrorMessage("Error interno del servidor. Por favor, intente más tarde.");
        }

        if (event.code !== 1000 && event.code !== 1001 && reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          console.log(`Intentando reconectar WS (intento ${reconnectAttempts}/${maxReconnectAttempts}) en ${delay/1000}s...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (wsRef.current?.readyState === WebSocket.CLOSED) initializeWebSocket();
          }, delay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          setErrorMessage("No se pudo restablecer la conexión después de varios intentos. Recargue la página.");
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("Error en WebSocket:", error);
      };

    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setErrorMessage('Error al crear la conexión WebSocket. Por favor, recargue la página.');
    }
  };

  // ---------------- Perfect Negotiation ----------------
  const createAndSendOffer = async () => {
    try {
      const pc = peerConnectionRef.current;
      const ws = wsRef.current;
      if (!pc || !ws || ws.readyState !== WebSocket.OPEN) return;

      if (pc.signalingState !== 'stable') {
        console.log("Signaling no estable, omito crear oferta:", pc.signalingState);
        // Retry si quedamos en have-local-offer
        if (pc.signalingState === 'have-local-offer') {
          setTimeout(() => {
            if (pc.signalingState === 'have-local-offer' && wsRef.current?.readyState === WebSocket.OPEN) {
              console.warn("[OFFER] have-local-offer >10s. Reenviando oferta actual.");
              wsRef.current.send(JSON.stringify({ type: 'offer', offer: pc.localDescription }));
            }
          }, 10000);
        }
        return;
      }

      makingOfferRef.current = true;
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
        voiceActivityDetection: true
      });
      await pc.setLocalDescription(offer);

      ws.send(JSON.stringify({ type: 'offer', offer }));
      console.log("Oferta enviada");
    } catch (error) {
      console.error("Error al crear/enviar oferta:", error);
    } finally {
      makingOfferRef.current = false;
    }
  };

  const handleOffer = async (offer) => {
    try {
      const pc = peerConnectionRef.current;
      const ws = wsRef.current;
      if (!pc) return;

      const offerDesc = new RTCSessionDescription(offer);
      const offerCollision = makingOfferRef.current || pc.signalingState !== 'stable';
      const shouldIgnore = !isPolite && offerCollision; // impolite ignora en colisión

      if (shouldIgnore) {
        console.log("[PN] Impolite y colisión: ignoro oferta entrante");
        return;
      }

      // Polite y colisión -> rollback
      if (offerCollision) {
        try {
          await pc.setLocalDescription({ type: 'rollback' });
          console.log("[PN] Rollback aplicado");
        } catch (e) {
          console.warn("[PN] Rollback falló o no era necesario:", e);
        }
      }

      await pc.setRemoteDescription(offerDesc);
      console.log("[SDP] RemoteDescription (offer) aplicada");

      // Drenar la cola de ICE
      await drainQueuedIce();

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log("[SDP] LocalDescription (answer) aplicada");

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'answer', answer }));
        console.log("[SDP] Answer enviada");
      }
    } catch (error) {
      console.error("[SDP] Error al procesar oferta (perfect-negotiation):", error);
    }
  };

  const handleAnswer = async (answer) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;

      if (pc.signalingState !== 'have-local-offer') {
        console.log("[SDP] No hay oferta local pendiente; ignoro answer. Signaling:", pc.signalingState);
        return;
      }

      await pc.setRemoteDescription(new RTCSessionDescription(answer));
      console.log("[SDP] RemoteDescription (answer) aplicada");

      // Drenar cola ICE
      await drainQueuedIce();
    } catch (error) {
      console.error("[SDP] Error al procesar respuesta:", error);
    }
  };

  const handleIceCandidate = async (candidate) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;

      const t = candidateTypeFromStr(candidate?.candidate || '');
      console.log(`[ICE] Candidato remoto [${t}]:`, candidate?.candidate);

      if (pc.remoteDescription) {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
        console.log("[ICE] Candidato añadido");
      } else {
        iceCandidatesQueue.current.push(candidate);
        console.log("[ICE] Candidato en cola (no hay remoteDescription). Cola:", iceCandidatesQueue.current.length);
      }
    } catch (error) {
      console.error("[ICE] Error al procesar candidato:", error);
    }
  };

  const handleSignalingMessage = async (data) => {
    try {
      switch (data.type) {
        case 'offer':
          await handleOffer(data.offer || data.description);
          break;
        case 'answer':
          await handleAnswer(data.answer || data.description);
          break;
        case 'ice-candidate':
          await handleIceCandidate(data.candidate);
          break;
        case 'chat':
        case 'chat-message': {
          const isOwnMessage = (data.sender === 'doctor' && !isPatient) ||
                               (data.sender === 'patient' && isPatient);
          const messageExists = chatMessages.some(msg => msg.id === data.id);
          if (!messageExists) {
            setChatMessages(prev => [...prev, { ...data, isMine: isOwnMessage, status: 'sent' }]);
            if (!isOwnMessage && !chatOpen) {
              setUnreadMessages(prev => prev + 1);
              setHasUnreadMessages(true);
              setLastMessageTime(new Date());
              setShowNotification(true);
              setTimeout(() => setShowNotification(false), 5000);
            }
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
              }
            }, 100);
          }
          break;
        }
        case 'join':
        case 'user_joined': {
          console.log('Usuario se unió:', data.user_id || '(sin id)');
          if (!remoteReadyRef.current) {
            remoteReadyRef.current = true;
            console.log('[Presence] remoteReady = true');
          }
          if (!isPatient && pendingOfferRef.current) {
            if (peerConnectionRef.current?.signalingState === 'stable') {
              console.log('[Presence] Enviando oferta pendiente tras user_joined');
              pendingOfferRef.current = false;
              await createAndSendOffer();
            } else {
              console.log('[Presence] No estable (', peerConnectionRef.current?.signalingState, '), mantengo pendingOffer');
            }
          }
          break;
        }
        case 'user_connected':
          console.log('Usuario conectado:', data.message);
          break;
        case 'user_disconnected':
          console.log('Usuario desconectado:', data.message);
          break;
        case 'hang-up':
          console.log('El otro usuario ha colgado.');
          setErrorMessage('La otra persona ha finalizado la llamada.');
          setTimeout(() => {
            cleanup();
            onEndCall();
          }, 3000);
          break;
        default:
          console.log('Tipo de mensaje desconocido:', data.type);
      }
    } catch (error) {
      console.error(`Error procesando mensaje de señalización tipo ${data.type}:`, error);
    }
  };

  // ---------------- SSL role / reconexión ----------------
  const handleFailedButIceConnected = () => {
    console.log("Detectado 'failed' pero ICE 'connected' -> intentar recuperación...");

    if (remoteVideoRef.current?.srcObject) {
      remoteVideoRef.current.muted = false;
      remoteVideoRef.current.volume = 1.0;
      remoteVideoRef.current.play().catch(e => console.warn("Error play en failed/connected:", e));
    }

    const pc = peerConnectionRef.current;
    if (!pc) return;

    if (pc.connectionState === 'failed' && pc.iceConnectionState === 'connected') {
      console.log("Reconstrucción suave por mismatch de estados (SSL role típico)...");
      try { pc.close(); } catch (_) {}
      peerConnectionRef.current = null;
      setTimeout(() => {
        if (createPeerConnRef.current) createPeerConnRef.current();
      }, 1000);
      return;
    }

    if (!isPatient) {
      try {
        if (typeof pc.restartIce === 'function') {
          pc.restartIce();
        }
      } catch (e) {
        console.warn("restartIce no disponible o falló:", e);
      }

      setTimeout(async () => {
        try {
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
          if (pc.signalingState !== 'stable') {
            console.log("Signaling no estable en failed/connected; omito oferta");
            return;
          }
          makingOfferRef.current = true;
          const offer = await pc.createOffer({
            iceRestart: true,
            offerToReceiveAudio: true,
            offerToReceiveVideo: true
          });
          await pc.setLocalDescription(offer);
          wsRef.current.send(JSON.stringify({ type: 'offer', offer }));
          console.log("Oferta con iceRestart enviada (failed/connected)");
        } catch (err) {
          console.error("Error al renegociar en failed/connected:", err);
          if (String(err?.message || '').includes('SSL role')) {
            handleReconnect();
          }
        } finally {
          makingOfferRef.current = false;
        }
      }, 1200);
    } else {
      console.log("Paciente en failed/connected -> espera oferta del doctor (impolite)");
    }
  };

  // ---------------- Reconnect helpers ----------------
  const handleReconnect = () => {
    const needsForceReconnect =
      connectionStatus === 'error' ||
      (peerConnectionRef.current?.connectionState === 'failed' &&
       peerConnectionRef.current?.iceConnectionState !== 'connected');

    if (needsForceReconnect) {
      console.log("Reconexión completa...");
      setErrorMessage("Reiniciando conexión completa...");
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setTimeout(() => initializeWebRTC(), 1000);
    } else {
      console.log("Reinicio suave de conexión...");
      setErrorMessage("Reiniciando conexión...");
      restartConnection();
    }
    setTimeout(() => setErrorMessage(""), 5000);
  };

  const restartConnection = () => {
    try {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.restartIce();
        if (!isPatient) createAndSendOffer();
      }
    } catch (e) {
      console.warn("restartConnection error:", e);
    }
  };

  // ---------------- Controls ----------------
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  // Compartir pantalla
  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        if (!navigator.mediaDevices?.getDisplayMedia) {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          throw new Error(
            isMobile
              ? "La función de compartir pantalla no está disponible en este dispositivo móvil."
              : "Este navegador no soporta compartir pantalla o requiere HTTPS."
          );
        }
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: "always", displaySurface: "monitor" },
          audio: false
        });
        const screenTrack = screenStream.getVideoTracks()[0];
        if (!screenTrack) throw new Error("No se obtuvo el track de pantalla");

        const videoSender = peerConnectionRef.current
          ?.getSenders()
          .find(s => s.track && s.track.kind === 'video');
        if (!videoSender) {
          screenStream.getTracks().forEach(t => t.stop());
          throw new Error("Error técnico: No se encontró el sender de video");
        }

        originalVideoTrackRef.current = localStreamRef.current?.getVideoTracks()[0] || null;
        await videoSender.replaceTrack(screenTrack);

        screenStreamRef.current = screenStream;
        setIsScreenSharing(true);

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }

        screenTrack.onended = () => {
          stopScreenSharing();
        };
      } else {
        await stopScreenSharing();
      }
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        setErrorMessage("No se concedieron permisos para compartir pantalla. Por favor, inténtelo de nuevo.");
      } else if (error.name === 'NotSupportedError') {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setErrorMessage(
          isMobile
            ? "Compartir pantalla no está disponible en este dispositivo."
            : "Su navegador no soporta compartir pantalla (use Chrome/Edge/Firefox actualizado)."
        );
      } else {
        setErrorMessage(`Error al compartir pantalla: ${error.message}`);
      }
      setIsScreenSharing(false);
    }
  };

  const stopScreenSharing = async () => {
    try {
      const videoSender = peerConnectionRef.current
        ?.getSenders()
        .find(s => s.track && s.track.kind === 'video');

      if (videoSender && originalVideoTrackRef.current) {
        await videoSender.replaceTrack(originalVideoTrackRef.current);
      }

      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }

      if (localVideoRef.current && localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }

      setIsScreenSharing(false);
    } catch (error) {
      setErrorMessage("Error al detener la compartición de pantalla. Por favor, recargue la página.");
      setIsScreenSharing(false);
    }
  };

  const requestFullscreenLandscape = async () => {
    try {
      await document.documentElement.requestFullscreen?.();
      await screen.orientation?.lock?.('landscape');
    } catch (e) { console.warn(e); }
  };

  // Reemplaza tu switchCamera actual por este
  const switchCamera = async () => {
    try {
      const pc = peerConnectionRef.current;
      const oldStream = localStreamRef.current;

      // Determina la cámara siguiente
      const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
      setFacingMode(newFacingMode);

      // Mantén el audio actual si está vivo
      const oldAudioTrack = oldStream?.getAudioTracks?.()[0];
      const audioSender = pc?.getSenders?.().find(s => s.track && s.track.kind === 'audio');

      // 1) Captura SOLO video de la cámara opuesta (no toques audio)
      const newVideoStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: { ideal: newFacingMode }
        },
        audio: false
      });
      const newVideoTrack = newVideoStream.getVideoTracks()[0];
      if (!newVideoTrack) throw new Error('No se obtuvo el track de video al cambiar de cámara');

      // 2) Reemplaza el track de video en el sender
      const videoSender = pc?.getSenders?.().find(s => s.track && s.track.kind === 'video');
      if (!videoSender) throw new Error('No se encontró el sender de video');
      const oldVideoTrack = videoSender.track;
      await videoSender.replaceTrack(newVideoTrack);

      // 3) Reconstruye el MediaStream local sin tocar el audio (o recuperándolo si murió)
      const combined = new MediaStream();
      combined.addTrack(newVideoTrack);

      if (oldAudioTrack && oldAudioTrack.readyState === 'live') {
        combined.addTrack(oldAudioTrack);
      } else {
        // El audio se detuvo (p. ej., por la lógica anterior): recupéralo y reemplázalo
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 2
          },
          video: false
        });
        const newAudioTrack = audioStream.getAudioTracks()[0];
        if (newAudioTrack) {
          if (audioSender) {
            await audioSender.replaceTrack(newAudioTrack);
          }
          combined.addTrack(newAudioTrack);
        }
      }

      // 4) Actualiza refs/UI
      localStreamRef.current = combined;
      if (localVideoRef.current) localVideoRef.current.srcObject = combined;

      // 5) Libera SOLO el video anterior (no toques audio)
      try { oldVideoTrack?.stop(); } catch {}

      // Opcional: en iOS Safari, a veces ayuda un pequeño “nudge” tras reemplazar
      // setTimeout(() => pc?.getSenders()?.forEach(s => s.track && s.track.applyConstraints?.({})), 0);

    } catch (error) {
      console.error('Error al cambiar de cámara:', error);
      setErrorMessage(`No se pudo cambiar de cámara: ${error.message}`);
    }
  };


  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };

  const toggleChat = () => {
    if (!chatOpen) {
      setUnreadMessages(0);
      setHasUnreadMessages(false);
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 300);
    }
    setChatOpen(!chatOpen);
  };

  const sendChatMessage = () => {
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    try {
      const messageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const messageData = {
        type: 'chat',
        id: messageId,
        message: newMessage.trim(),
        sender: isPatient ? 'patient' : 'doctor',
        timestamp: new Date().toISOString()
      };
      const localMessage = { ...messageData, isMine: true, status: 'sending' };
      setChatMessages(prev => [...prev, localMessage]);
      setPendingMessages(prev => ({ ...prev, [messageId]: true }));
      setNewMessage('');
      wsRef.current.send(JSON.stringify(messageData));
      setTimeout(() => {
        setChatMessages(prev => prev.map(msg => msg.id === messageId ? { ...msg, status: 'sent' } : msg));
        setPendingMessages(prev => {
          const updated = { ...prev };
          delete updated[messageId];
          return updated;
        });
      }, 400);
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
      setChatMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = { ...last, status: 'error' };
        }
        return updated;
      });
    }
  };

  const resendMessage = (message) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    try {
      setChatMessages(prev => prev.map(msg => msg.id === message.id ? { ...msg, status: 'sending' } : msg));
      setPendingMessages(prev => ({ ...prev, [message.id]: true }));
      const messageData = {
        type: 'chat',
        id: message.id,
        message: message.message,
        sender: message.sender,
        timestamp: message.timestamp
      };
      wsRef.current.send(JSON.stringify(messageData));
      setTimeout(() => {
        setChatMessages(prev => prev.map(msg => msg.id === message.id ? { ...msg, status: 'sent' } : msg));
        setPendingMessages(prev => {
          const updated = { ...prev };
          delete updated[message.id];
          return updated;
        });
      }, 500);
    } catch (error) {
      setChatMessages(prev => prev.map(msg => msg.id === message.id ? { ...msg, status: 'error' } : msg));
    }
  };

  const enableAudioOnly = async () => {
    localStreamRef.current?.getVideoTracks().forEach(t => t.enabled = false);
    const aSender = peerConnectionRef.current?.getSenders().find(s => s.track?.kind === 'audio');
    if (aSender) {
      const p = aSender.getParameters();
      if (!p.encodings) p.encodings = [{}];
      p.encodings[0].maxBitrate = 32_000;
      await aSender.setParameters(p);
    }
  };

  const handleUserInteraction = () => {
    try {
      console.log("Interacción de usuario detectada, activando audio/video...");
      setUserInteracted(true);

      const v = remoteVideoRef.current;
      if (v && v.srcObject) {
        // Quita el mute y vuelve a reproducir
        setRemoteAutoMuted(false);
        v.muted = false;
        v.volume = 1.0;

        const attemptPlay = (attempts = 0) => {
          v.play()
            .then(() => {
              if (v.muted) v.muted = false;
            })
            .catch(error => {
              if (error.name === "NotAllowedError") {
                // Si vuelve a bloquearse, mantenemos el botón visible
                setNeedsReplay(true);
              } else if (attempts < 5) {
                setTimeout(() => attemptPlay(attempts + 1), 700);
              } else {
                console.error("No se pudo reproducir después de múltiples intentos:", error);
                setNeedsReplay(true);
              }
            });
        };
        attemptPlay();
      }
    } catch (error) {
      console.error("Error en handleUserInteraction:", error);
    }
  };


  // ---------------- Wake lock & Keyboard safe area ----------------
  useEffect(() => {
    let wakeLock;
    const requestWakeLock = async () => {
      try { wakeLock = await navigator.wakeLock?.request('screen'); } catch (_) {}
    };
    requestWakeLock();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    });
    return () => { try { wakeLock?.release(); } catch (_) {} };
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      const bottomOffset = Math.max(0, vv.height + vv.offsetTop - window.innerHeight);
      const el = chatContainerRef.current?.parentElement;
      if (el) el.style.marginBottom = `${bottomOffset + 16}px`;
    };
    vv.addEventListener('resize', handler);
    return () => vv.removeEventListener('resize', handler);
  }, []);

  // ---------------- End Call ----------------
  const handleEndCallClick = () => {
    setEndCallDialogOpen(true);
  };

  const confirmEndCall = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'hang-up' }));
    }
    cleanup();
    setEndCallDialogOpen(false);
    setTimeout(() => {
      onEndCall(isPatient);
    }, 300);
  };

  const cleanup = () => {
    console.log('Limpiando recursos...');
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (screenStreamRef.current) {
      try { screenStreamRef.current.getTracks().forEach(t => t.stop()); } catch {}
      screenStreamRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Track detenido:', track.kind);
      });
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      console.log('Peer connection cerrado');
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Usuario finalizó la llamada');
      console.log('WebSocket cerrado');
    }
    try {
      localStorage.removeItem(`chat_messages_${roomId}`);
    } catch (error) {
      console.error("Error al limpiar mensajes de localStorage:", error);
    }
  };

  // ---------------- Status chip helpers ----------------
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'connecting': return 'warning';
      case 'disconnected':
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'connected': return 'Conectado';
      case 'connecting': return 'Conectando...';
      case 'disconnected': return 'Desconectado';
      case 'error': return 'Error de conexión';
      default: return 'Estado desconocido';
    }
  };

  // ---------------- Render ----------------
  return (
    <VideoContainer>
      {/* Estado de conexión (WebRTC) */}
      <Box sx={{
        position: 'absolute',
        top: `calc(16px + env(safe-area-inset-top))`,
        left: 16,
        zIndex: 20,
      }}>
        <StatusChip
          label={getStatusLabel(connectionStatus)}
          color={getStatusColor(connectionStatus)}
          size="medium"
          variant="filled"
        />
      </Box>

      {/* Grabación */}
      {isRecording && (
        <Box sx={{ position: 'absolute', top: 60, left: 16, zIndex: 20 }}>
          <StatusChip
            icon={<FiberManualRecordIcon />}
            label="Grabando"
            color="error"
            size="medium"
            variant="filled"
          />
        </Box>
      )}

      {/* Nombre del participante */}
      <Box sx={{
        position: 'absolute',
        top: { xs: 60, sm: 16 },
        right: 16,
        zIndex: 20,
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: '8px 16px',
        borderRadius: '20px',
        maxWidth: { xs: 'calc(100% - 32px)', sm: 'auto' }
      }}>
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            fontSize: { xs: '0.9rem', sm: '1.25rem' }
          }}
        >
          {roleLabel}: {participantName}
        </Typography>
      </Box>

      {/* Mensaje de orientación */}
      {showOrientationMessage && (
        <Box sx={{
          position: 'absolute',
          top: 70,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: 1,
          textAlign: 'center',
          zIndex: 1000
        }}>
          <Typography variant="body2">
            Puedes girar tu dispositivo para una mejor experiencia
          </Typography>
        </Box>
      )}

      {/* Indicador de compartir pantalla */}
      {isScreenSharing && (
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 20,
        }}>
          <ScreenShareIcon fontSize="small" />
          <Typography variant="caption">Compartiendo pantalla</Typography>
        </Box>
      )}

      {/* Video remoto */}
      <MainVideoBox>
        <VideoElement 
        ref={remoteVideoRef} 
        autoPlay 
        playsInline 
        muted={remoteAutoMuted}
      />

        {connectionStatus !== 'connected' && !errorMessage && (
          <Box sx={{
            position: 'absolute',
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            padding: 3,
            borderRadius: 4,
            zIndex: 14,
          }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              Esperando al paciente...
            </Typography>
            <Typography variant="body1" sx={{ color: 'white' }}>
              Tiempo de espera: {formatWaitingTime(waitingTime)}
            </Typography>
            <CircularProgress sx={{ mt: 2, color: 'white' }} />
            <Button
              variant="outlined"
              color="inherit"
              sx={{ mt: 2, color: 'white', borderColor: 'white' }}
              onClick={handleEndCallClick}
            >
              Cancelar espera
            </Button>
          </Box>
        )}

        {connectionStatus === 'connected' && !userInteracted && (
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 15,
            }}
          >
            <Typography variant="h5" color="white" gutterBottom>
              Listo para conectar
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleUserInteraction}
            >
              Haga clic aquí para entrar y activar el audio
            </Button>
          </Box>
        )}

        {needsReplay && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0,0,0,0.8)',
              padding: 3,
              borderRadius: 2,
              zIndex: 20,
            }}
          >
            <Typography variant="h6" color="white" gutterBottom>
              Se requiere activar el audio
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={handleUserInteraction}
              startIcon={<VolumeUpIcon />}
            >
              Activar Audio
            </Button>
          </Box>
        )}

        {errorMessage && (
          <Box sx={{
            position: 'absolute',
            textAlign: 'center',
            backgroundColor: 'rgba(211,47,47,0.8)',
            padding: 3,
            borderRadius: 4,
            maxWidth: '80%',
            zIndex: 16,
          }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              Error de conexión
            </Typography>
            <Typography variant="body1" sx={{ color: 'white' }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="error"
              sx={{ mt: 2 }}
              onClick={onEndCall}
            >
              Volver
            </Button>
          </Box>
        )}
      </MainVideoBox>

      {showNotification && lastMessageTime && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 100,
            right: 16,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 2,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              transform: 'translateY(-3px)'
            }
          }}
          onClick={toggleChat}
        >
          <ChatIcon fontSize="small" />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Nuevo mensaje
            </Typography>
            <Typography variant="caption">
              {lastMessageTime.toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Video local */}
      <LocalVideoBox>
        <VideoElement ref={localVideoRef} autoPlay playsInline muted />
        {isVideoOff && (
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.800'
            }}
          >
            <VideocamOffIcon sx={{ fontSize: 48, color: 'white' }} />
          </Box>
        )}
      </LocalVideoBox>

      {/* Controles */}
      <ControlsBox>
        {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
          <Tooltip title="Pantalla completa">
            <ControlButton onClick={requestFullscreenLandscape}>
              <FullscreenIcon />
            </ControlButton>
          </Tooltip>
        )}

        <Tooltip title={isMuted ? "Activar micrófono" : "Silenciar micrófono"}>
          <ControlButton onClick={toggleMute}>
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </ControlButton>
        </Tooltip>

        <Tooltip title={isVideoOff ? "Activar cámara" : "Desactivar cámara"}>
          <ControlButton onClick={toggleVideo}>
            {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
          </ControlButton>
        </Tooltip>

        <Tooltip title={
          !screenShareSupported
            ? "Compartir pantalla no disponible en este dispositivo"
            : isScreenSharing
              ? "Detener compartir pantalla"
              : "Compartir pantalla"
        }>
          <span>
            <ControlButton
              onClick={toggleScreenShare}
              disabled={!screenShareSupported}
              sx={{
                opacity: !screenShareSupported ? 0.5 : 1,
                cursor: !screenShareSupported ? 'not-allowed' : 'pointer'
              }}
            >
              {isScreenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
            </ControlButton>
          </span>
        </Tooltip>

        {/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
          <Tooltip title="Cambiar cámara">
            <ControlButton onClick={switchCamera}>
              <FlipCameraIcon />
            </ControlButton>
          </Tooltip>
        )}

        <Tooltip title="Chat">
          <Badge
            badgeContent={unreadMessages}
            color="error"
            invisible={unreadMessages === 0}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.8rem',
                height: '20px',
                minWidth: '20px',
              }
            }}
          >
            <ControlButton
              onClick={toggleChat}
              sx={{
                backgroundColor: chatOpen ? theme.palette.primary.main : theme.palette.grey[800],
                '&:hover': {
                  backgroundColor: chatOpen ? theme.palette.primary.dark : theme.palette.grey[700],
                }
              }}
            >
              <ChatIcon />
            </ControlButton>
          </Badge>
        </Tooltip>

        <Tooltip title={connectionStatus === 'error' ? "Forzar reconexión completa" : "Reiniciar conexión"}>
          <ControlButton
            onClick={handleReconnect}
            sx={{
              backgroundColor: connectionStatus === 'error'
                ? theme.palette.error.dark
                : theme.palette.grey[800],
              '&:hover': {
                backgroundColor: connectionStatus === 'error'
                  ? theme.palette.error.main
                  : theme.palette.grey[700],
              }
            }}
          >
            <RefreshIcon />
          </ControlButton>
        </Tooltip>

        <Tooltip title="Diagnóstico de conexión">
          <ControlButton
            onClick={() => {
              const diagnosticInfo = {
                browserInfo: navigator.userAgent,
                webRTCSupported: !!window.RTCPeerConnection,
                mediaDevicesSupported: !!navigator.mediaDevices,
                connectionState: peerConnectionRef.current?.connectionState || 'no connection',
                iceConnectionState: peerConnectionRef.current?.iceConnectionState || 'no connection',
                signalingState: peerConnectionRef.current?.signalingState || 'no connection',
                localStreamTracks: localStreamRef.current
                  ? localStreamRef.current.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, id: t.id }))
                  : 'no stream',
                remoteStreamTracks: remoteVideoRef.current && remoteVideoRef.current.srcObject
                  ? remoteVideoRef.current.srcObject.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, id: t.id }))
                  : 'no stream',
                webSocketState: wsRef.current ? wsRef.current.readyState : 'no websocket'
              };
              console.log("Diagnóstico de conexión:", diagnosticInfo);
              alert(`
Estado de conexión: ${diagnosticInfo.connectionState}
Estado ICE: ${diagnosticInfo.iceConnectionState}
Estado WebSocket: ${diagnosticInfo.webSocketState === 1 ? 'Conectado' : 'Desconectado'}
Stream local: ${diagnosticInfo.localStreamTracks !== 'no stream'
    ? `${diagnosticInfo.localStreamTracks.length} tracks`
    : 'no stream'}
Stream remoto: ${diagnosticInfo.remoteStreamTracks !== 'no stream'
    ? `${diagnosticInfo.remoteStreamTracks.length} tracks`
    : 'no stream'}
              `);
              if (diagnosticInfo.connectionState !== 'connected' ||
                  diagnosticInfo.iceConnectionState !== 'connected' ||
                  diagnosticInfo.webSocketState !== 1) {
                if (confirm("Se detectaron problemas de conexión. ¿Desea intentar reiniciar la conexión?")) {
                  restartConnection();
                }
              }
            }}
          >
            <BugReportIcon />
          </ControlButton>
        </Tooltip>

        <Tooltip title="Finalizar consulta">
          <EndCallButton onClick={handleEndCallClick}>
            <CallEndIcon />
          </EndCallButton>
        </Tooltip>
      </ControlsBox>

    
      {/* Panel de Chat */}
      {chatOpen && (
        <Box sx={{
          position: 'absolute',
          bottom: { xs: 120, sm: 120 },
          right: { xs: 16, sm: 24 },
          width: { xs: 'min(92vw, 420px)', sm: 350 },
          maxWidth: { xs: 'min(92vw, 420px)', sm: 350 },
          height: { xs: 400, sm: 450 },
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper,
          borderRadius: 3,
          boxShadow: theme.shadows[10],
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 100,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          animation: 'fadeIn 0.2s ease-out',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(20px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' }
          }
        }}>
          {/* Header */}
          <Box sx={{
            p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.dark, 0.2)
              : alpha(theme.palette.primary.light, 0.1)
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChatIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Chat</Typography>
            </Box>
            <IconButton onClick={toggleChat} size="small"
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.2),
                '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.3) }
              }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Lista mensajes */}
          <Box
            ref={chatContainerRef}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.background.default, 0.5)
            }}
          >
            {chatMessages.length === 0 ? (
              <Box sx={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: '100%', opacity: 0.7
              }}>
                <ChatIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" align="center">
                  No hay mensajes aún.
                  <br />
                  Envía el primer mensaje.
                </Typography>
              </Box>
            ) : (
              chatMessages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    alignSelf: msg.isMine ? 'flex-end' : 'flex-start',
                    backgroundColor: msg.isMine
                      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.1)
                      : alpha(theme.palette.grey[300], theme.palette.mode === 'dark' ? 0.2 : 0.5),
                    borderRadius: 2,
                    p: 1.5,
                    maxWidth: '85%',
                    position: 'relative',
                    wordBreak: 'break-word',
                    boxShadow: theme.shadows[1]
                  }}
                >
                  <Typography variant="body2">{msg.message}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5 }}>
                    {msg.isMine && (
                      <>
                                              {msg.status === 'sending' && (
                          <CircularProgress
                            size={10}
                            thickness={8}
                            sx={{ mr: 0.5, color: 'text.secondary' }}
                          />
                        )}
                        {msg.status === 'sent' && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: '0.7rem',
                              color: 'success.main',
                              mr: 0.5,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            ✓
                          </Box>
                        )}
                        {msg.status === 'error' && (
                          <Tooltip title="Error al enviar. Haz clic para reintentar.">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => resendMessage(msg)}
                              sx={{ p: 0, mr: 0.5, width: 14, height: 14 }}
                            >
                              <RefreshIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.7rem' }}
                    >
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              gap: 1,
              backgroundColor: theme.palette.background.paper
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Escribe un mensaje..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage();
                }
              }}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
            />
            <IconButton
              color="primary"
              onClick={sendChatMessage}
              disabled={!newMessage.trim()}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '&:hover': { bgcolor: theme.palette.primary.dark },
                '&.Mui-disabled': {
                  bgcolor: alpha(theme.palette.primary.main, 0.4),
                  color: 'white'
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Diálogo de finalización */}
      <Dialog
        open={endCallDialogOpen}
        onClose={() => setEndCallDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Finalizar Consulta</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            ¿Está seguro que desea finalizar la consulta con {patientName}?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEndCallDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 20 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmEndCall}
            color="error"
            variant="contained"
            sx={{ borderRadius: 20 }}
          >
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>
    </VideoContainer>
  );
};

export default VideoCallComponent;